import { useState } from 'react';
import {
  apiErrorText,
  useCreateTenderEstimateMutation,
  useListIndexOptionsQuery,
  useListSmetaContextOptionsQuery,
  useListVolumeChecksQuery,
} from '@shared/api';
import { zoneLabel } from '../PriceZoneField';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label } from '@shared/ui/Input';
import { useAgentPermission } from '@entities/user';
import {
  Err,
  FileHint,
  FormRow,
  FormSelect,
  Select,
  SubmitWrapper,
} from './styled';
import { TERRITORY_HINT, TERRITORY_OPTIONS } from '../territoryCategory';

interface TenderCreateFormProps {
  onCreated: (checkId: string) => void;
}

/** Какие индексы есть у региона — подсказка в списке выбора. */
function regionIndexHint(r: { rim: boolean; bim: boolean }): string {
  if (r.rim && r.bim) return '';
  if (r.rim) return ' — только индексы РИМ (ГЭСН, ФСБЦ)';
  return ' — только индексы Минстроя (ФЕР, ФССЦ)';
}

export function TenderCreateForm({ onCreated }: TenderCreateFormProps) {
  const [objectName, setObjectName] = useState('');
  const [smetaCode, setSmetaCode] = useState('');
  const [region, setRegion] = useState('');
  const [priceZone, setPriceZone] = useState('');
  const [objectType, setObjectType] = useState('');
  const [inputKind, setInputKind] = useState('grand_smeta_skeleton');
  const [volumeCheckId, setVolumeCheckId] = useState('');
  const [volumeSource, setVolumeSource] = useState('rd');
  const [precontractId, setPrecontractId] = useState('');
  const [contractId, setContractId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [calcMode, setCalcMode] = useState('works_and_materials');
  // Категория местности — колонка НР (C05); пусто — не указана, ставка обычная.
  const [territory, setTerritory] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { can_write } = useAgentPermission('smeta');
  const volumeChecksQ = useListVolumeChecksQuery({ limit: 50 });
  const contextOptionsQ = useListSmetaContextOptionsQuery();
  const indexOptionsQ = useListIndexOptionsQuery();
  const [createEstimate, { isLoading: creating }] = useCreateTenderEstimateMutation();
  const indexRegions = indexOptionsQ.data?.regions ?? [];
  const objectTypes = indexOptionsQ.data?.object_types ?? [];
  // Ценовые зоны выбранного региона: список — только у многозонного региона.
  const zones = indexRegions.find((r) => r.region === region)?.zones ?? [];

  const volumeChecks = (volumeChecksQ.data?.items ?? []).filter((v) => v.status === 'completed');
  const contextContracts = contextOptionsQ.data?.contracts ?? [];
  const contextProjects = contextOptionsQ.data?.projects ?? [];
  const contextPrecontracts = contextOptionsQ.data?.precontracts ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (inputKind === 'volume_check') {
      if (!volumeCheckId) {
        setError('Выберите проверку объёмов (Функция 1) как источник позиций.');
        return;
      }
    } else if (!file) {
      setError('Приложите файл перечня работ тендера (Excel/PDF).');
      return;
    }
    const fd = new FormData();
    fd.append('object_name', objectName);
    if (smetaCode.trim()) fd.append('smeta_code', smetaCode);
    if (region.trim()) fd.append('region', region);
    if (region.trim() && priceZone) fd.append('price_zone', priceZone);
    if (objectType.trim()) fd.append('object_type', objectType);
    if (precontractId) fd.append('precontract_id', precontractId);
    if (contractId) fd.append('contract_id', contractId);
    if (projectId) fd.append('project_id', projectId);
    fd.append('input_kind', inputKind);
    fd.append('calc_mode', calcMode);
    if (territory) fd.append('territory_category', territory);
    if (inputKind === 'volume_check') {
      fd.append('volume_check_id', volumeCheckId);
      fd.append('volume_source', volumeSource);
    } else if (file) {
      fd.append('file', file);
    }
    try {
      const res = await createEstimate(fd).unwrap();
      onCreated(res.check_id);
      setObjectName('');
      setSmetaCode('');
      setRegion('');
      setPriceZone('');
      setObjectType('');
      setFile(null);
    } catch (err) {
      setError(
        apiErrorText(
          err,
          'Не удалось запустить расчёт. Проверьте формат файла и попробуйте снова.',
        ),
      );
      // eslint-disable-next-line no-console
      console.error('createTenderEstimate failed', err);
    }
  }

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <Field>
          <Label>Объект</Label>
          <Input
            placeholder="Например: Котельная, корпус КЖ"
            value={objectName}
            onChange={(e) => setObjectName(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Привязка к преддоговору / договору / объекту ПТО (необязательно)</Label>
          <FormRow>
            <FormSelect
              value={precontractId}
              onChange={(e) => setPrecontractId(e.target.value)}
            >
              <option value="">— преддоговор (тендер) —</option>
              {contextPrecontracts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.subject || p.counterparty || p.id.slice(0, 8)}
                </option>
              ))}
            </FormSelect>
            <FormSelect
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
            >
              <option value="">— договор —</option>
              {contextContracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.contract_number ? `№ ${c.contract_number}` : 'договор б/н'} ·{' '}
                  {(c.subject || c.counterparty || '').slice(0, 40)}
                </option>
              ))}
            </FormSelect>
            <FormSelect
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">— проект ПТО —</option>
              {contextProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code ? `${p.code} · ` : ''}
                  {p.name}
                </option>
              ))}
            </FormSelect>
          </FormRow>
        </Field>
        <Field>
          <Label>Шифр сметы (необязательно)</Label>
          <Input
            placeholder="Например: 02-01-01"
            value={smetaCode}
            onChange={(e) => setSmetaCode(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Регион (для пересчёта в текущие цены)</Label>
          <Select
            value={region}
            onChange={(e) => {
              setRegion(e.target.value);
              setPriceZone('');
            }}
          >
            <option value="">— без региона: индексы не применятся —</option>
            {indexRegions.map((r) => (
              <option key={r.region} value={r.region}>
                {r.region}
                {regionIndexHint(r)}
              </option>
            ))}
          </Select>
          {indexRegions.length === 0 && (
            <FileHint>
              Утверждённых индексов пока нет: позиции ФЕР и сборников цен 2001 останутся в
              базисе, ГЭСН и ФСБЦ не рассчитаются.
            </FileHint>
          )}
        </Field>
        {zones.length > 1 && (
          <Field>
            <Label>Ценовая зона</Label>
            <Select value={priceZone} onChange={(e) => setPriceZone(e.target.value)}>
              <option value="">— не выбрана: индексы зон не применятся —</option>
              {zones.map((z) => (
                <option key={z} value={z}>
                  {zoneLabel(z)}
                </option>
              ))}
            </Select>
            <FileHint>
              У региона индексы даны по ценовым зонам. Номер зоны объекта — в письме Минстроя и
              во ФГИС ЦС.
            </FileHint>
          </Field>
        )}
        <Field>
          <Label>Тип объекта (необязательно)</Label>
          {objectTypes.length > 0 ? (
            <Select value={objectType} onChange={(e) => setObjectType(e.target.value)}>
              <option value="">— не выбран —</option>
              {objectTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              placeholder="Тип объекта строительства — ключ БИМ-индекса ФЕР"
              value={objectType}
              onChange={(e) => setObjectType(e.target.value)}
            />
          )}
        </Field>
        <Field>
          <Label>Формат входа</Label>
          <Select value={inputKind} onChange={(e) => setInputKind(e.target.value)}>
            <option value="grand_smeta_skeleton">Скелет Гранд-Сметы (шифры без цен)</option>
            <option value="vor">Ведомость объёмов работ (ВОР, без шифров)</option>
            <option value="volume_check">Из проверки объёмов (Функция 1 — РД или контракт)</option>
          </Select>
        </Field>
        <Field>
          <Label>Что считаем</Label>
          <Select value={calcMode} onChange={(e) => setCalcMode(e.target.value)}>
            <option value="works_and_materials">Работы и материалы</option>
            <option value="works_only">
              Только работы — без материалов, машины и перевозки считаем
            </option>
          </Select>
        </Field>
        <Field>
          <Label>Категория местности</Label>
          <Select value={territory} onChange={(e) => setTerritory(e.target.value)}>
            <option value="">Не указана — ставка накладных расходов обычная</option>
            {TERRITORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <FileHint>{TERRITORY_HINT}</FileHint>
        </Field>
        {inputKind === 'volume_check' ? (
          <>
            <Field>
              <Label>Проверка объёмов (Функция 1)</Label>
              <Select value={volumeCheckId} onChange={(e) => setVolumeCheckId(e.target.value)}>
                <option value="">— выберите завершённую проверку объёмов —</option>
                {volumeChecks.map((vc) => (
                  <option key={vc.id} value={vc.id}>
                    {vc.object_name} ({new Date(vc.created_at).toLocaleDateString('ru-RU')})
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Сторона объёмов</Label>
              <Select value={volumeSource} onChange={(e) => setVolumeSource(e.target.value)}>
                <option value="rd">По рабочей документации (РД)</option>
                <option value="contract">По ведомости контракта (ВОР / РДЦ)</option>
              </Select>
            </Field>
          </>
        ) : (
          <Field>
            <Label>Перечень работ тендера</Label>
            <Input
              type="file"
              accept=".xlsx,.xlsm,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <FileHint>
              {file
                ? `Выбран файл: ${file.name}`
                : 'Excel-выгрузка Гранд-Сметы или ВОР (Excel/PDF).'}
            </FileHint>
          </Field>
        )}
        {error && <Err>{error}</Err>}
        <SubmitWrapper>
          <Button
            type="submit"
            $variant="primary"
            disabled={creating || !can_write}
            title={!can_write ? 'Только чтение' : undefined}
          >
            {creating ? 'Запуск…' : 'Рассчитать ЛСР'}
          </Button>
        </SubmitWrapper>
      </form>
    </Card>
  );
}
